import React, {useState, useEffect} from 'react';
import { Route, Routes } from 'react-router-dom';
import { Link } from "react-router-dom";

import { ThemeProvider, CssBaseline, Box, Button, Container, Paper, Typography, CircularProgress } from "@mui/material";
import theme from "./styles/theme";

// react-toastify imports
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DashboardLayout from "./layouts/DashboardLayout";
import MainLayout from './layouts/MainLayout';
import ErrorBoundary from './components/ErrorBoundary';

import ClubsManagementPage from './pages/admin/ClubsManagementPage';
import ClubManagementPage from './pages/ClubManagementPage';
import AthletesManagementPage from './pages/AthletesManagementPage';
import LoginPage from './pages/LoginPage';
import Register from "./pages/Register";
import DashboardPage from './pages/Dashboard';
import JudgesManagementPage from './pages/JudgesManagementPage';
import WithRole from './utils/withRole';
import OfficialsManagementPage from './pages/OfficialsManagementPage';
import CompetitionsListPage from './pages/admin/CompetitionsListPage';
import CompetitionFormPage from './pages/admin/CompetitionFormPage';
import ProfilePage from './pages/ProfilePage';
import CompetitionRegistrationPage from './pages/CompetitionRegistrationPage';
import AthleteRegistrationPage from './pages/AthleteRegistrationPage';
import JudgeRegistrationPage from './pages/JudgeRegistrationPage';
import OfficialRegistrationPage from './pages/OfficialRegistrationPage';
import RegistrationSummaryPage from './pages/RegistrationSummaryPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import OtherBookingsPage from './pages/OtherBookingsPage';

import axios from "./utils/axiosConfig";
import CompetitionDashboard from './pages/CompetitionDashboard';

import { useParams } from "react-router-dom";
import SavedRegistrationSummaryPage from './pages/SavedRegistrationSummaryPage';
import RegistrationConfirmationForm from './pages/RegistrationConfirmationForm';
import RegistrationConfirmationPage from './pages/RegistrationConfirmationPage';
import EmailEditorPage from './pages/EmailEditorPage';
import UsersManagementPage from './pages/admin/UsersManagementPage';
import TrampolineScorecardPage from './pages/TrampolineScorecardPage';

import Logo from './components/Logo';
import RegisteredCompetitionPage from './pages/RegisteredCompetitionPage';
import CompetitionCardsPage from './pages/CompetitionCardsPage';
import SchedulePage from './pages/SchedulePage';
import CompetitionInfoPage from './pages/CompetitionInfoPage';

import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CardEditPage from './pages/CardEditPage';

import DevInfo from './components/DevInfo';
import logger from './utils/logger';

const Home: React.FC = () => {
  return <LoginPage />;
};

// Erstelle eine Wrapper-Komponente
function CompetitionDashboardWrapper() {
  // Lese die URL-Parameter aus
  const { competitionId } = useParams();
  
  return (
    <WithRole allowedRoles={['admin','organizer','helper']}>
      <CompetitionDashboard competitionId={competitionId || ''} />
    </WithRole>
  );
}

const AppContent: React.FC = () => {
  const { userRole, loading } = useAuth();
  const [showDevInfo, setShowDevInfo] = useState(false);

  // Check ob DevInfo angezeigt werden soll basierend auf Backend
  useEffect(() => {
    const checkDevInfoVisibility = async () => {
      try {
        const response = await axios.get('/api/dev-info');
        const backendSaysProduction = response.data.isProduction;
        const forceShow = process.env.REACT_APP_SHOW_DEV_INFO === 'true';
        logger.info('DevInfo visibility check', {
          backendSaysProduction,
          forceShow,
          showDevInfo: !backendSaysProduction || forceShow
        });
        setShowDevInfo(!backendSaysProduction || forceShow);
      } catch (error) {
        // Bei Fehler nur anzeigen wenn explizit erzwungen
        setShowDevInfo(process.env.REACT_APP_SHOW_DEV_INFO === 'true');
      }
    };

    checkDevInfoVisibility();
  }, []); 
  
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Anmeldung wird überprüft...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* Entfernt Standard-Browser-Styling */}
      
      {/* Dev-Info Leiste */}
      <DevInfo />
      
      {/* Haupt-Content mit dynamischem Abstand für DevInfo */}
      <Box sx={{ 
        pt: showDevInfo ? '40px' : 0, // Platz für DevInfo nur wenn sichtbar
        minHeight: '100vh'
      }}>

      <ErrorBoundary>
      <Routes>
        {/* Öffentliche Routen */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/card-edit/:token" element={<CardEditPage />} />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Geschützte Routen */}
        <Route element={<MainLayout />}>
          <Route path="/dashboard" element={
              <WithRole allowedRoles={['user', 'admin', 'organizer','helper']}> 
                <DashboardPage />
                </WithRole>
          } />
          <Route path="/club" element={
              <WithRole allowedRoles={['user', 'admin', 'organizer']}> 
                <ClubManagementPage />
                </WithRole>
          } />
          <Route path="/club/athletes" element={
              <WithRole allowedRoles={['user']}> 
                <AthletesManagementPage />
                </WithRole>
          } />
          <Route path="/club/judges" element={
              <WithRole allowedRoles={['user']}> 
                <JudgesManagementPage />
                </WithRole>
          } />
          <Route path="/club/officials" element={
              <WithRole allowedRoles={['user']}> 
                <OfficialsManagementPage />
                </WithRole>
          } />
          <Route path="/club/profile" element={
              <WithRole allowedRoles={['user', 'admin', 'organizer', 'helper']}>
                <ProfilePage />
              </WithRole>
          } /> 
          <Route path="/club/manage" element={<ClubManagementPage />} />     
          {/*Admin Bereich*/}
          <Route path="/admin/clubs" element={
              <WithRole allowedRoles={['admin']}> 
                <ClubsManagementPage />
              </WithRole>
            }/>
            <Route path="/admin/users" element={
                <WithRole allowedRoles={['admin']}> 
                  <UsersManagementPage />
                </WithRole>
              }/>
          <Route path="/admin/competitions" element={
              <WithRole allowedRoles={['admin', 'organizer']}> 
                <CompetitionsListPage />
                </WithRole>
          } />
          <Route path="/admin/competitions/new" element={
              <WithRole allowedRoles={['admin', 'organizer']}> 
                <CompetitionFormPage />
                </WithRole>
          } />
          <Route path="/admin/competitions/edit/:id" element={
              <WithRole allowedRoles={['admin', 'organizer']}> 
                <CompetitionFormPage />
                </WithRole>
          } />
          <Route path="/competition-registration/:competitionId" element={
            <WithRole allowedRoles={['user']}>
              <CompetitionRegistrationPage />
            </WithRole>}>
              <Route path="athletes" element={<AthleteRegistrationPage />} />
              <Route path="judges" element={<JudgeRegistrationPage />} />
              <Route path="officials" element={<OfficialRegistrationPage />} />
              <Route path="additional" element={<OtherBookingsPage/>} />
              <Route path="summary" element={<RegistrationSummaryPage />} />
          </Route>
          *<Route path="/registered-competition/:competitionId" element={
            <WithRole allowedRoles={['user']}>
              <RegisteredCompetitionPage />
            </WithRole>
          }>
            <Route path="summary" element={<RegistrationSummaryPage />} />
            <Route path="cards" element={<CompetitionCardsPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="info" element={<CompetitionInfoPage />} />
          </Route>*
          <Route path="/orga/competition/:competitionId" element={<CompetitionDashboardWrapper />} />          
          <Route path="/orga/competition/:competitionId/club/:clubId/current-summary" element={
            <WithRole allowedRoles={['admin', 'organizer', 'helper']}>
              <RegistrationSummaryPage />
              </WithRole>} />
          <Route path="/orga/competition/:competitionId/club/:clubId/saved-summary" element={
            <WithRole allowedRoles={['admin', 'organizer', 'helper']}>
              <RegistrationSummaryPage />
              </WithRole>} />
          <Route path="/orga/competition/:competitionId/confirmation/:clubId" element={
            <WithRole allowedRoles={['admin', 'organizer', 'helper']}>
              <RegistrationConfirmationForm />
            </WithRole>} />
          <Route path="/orga/competition/:competitionId/confirmation-registration/:confirmationId" element={
            <WithRole allowedRoles={['admin', 'organizer', 'helper']}>
              <RegistrationConfirmationPage />
            </WithRole>} />
          <Route path="/orga/competition/:competitionId/confirmation-registration/:confirmationId/send-email" element={
            <WithRole allowedRoles={['admin', 'organizer', 'helper']}>
              <EmailEditorPage registrationConfirmationId="" />
            </WithRole>} />
          <Route path="*" element={
              <WithRole allowedRoles={['user', 'admin', 'organizer', 'helper']}> 
                <DashboardPage />
              </WithRole>
          } />

        </Route>
      </Routes>
      </ErrorBoundary>
      </Box>
      {/* Toast Container für Benachrichtigungen */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ top: '74px' }}
      />
    </ThemeProvider>
  );
};

// Die Haupt-App-Komponente
const App: React.FC = () => {
  return <AppContent />;
};

export default App;